import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import AIFilterChips from '@/storybook/components/AiChatDrawer/components/AIFilterChips/AIFilterChips';
import Text from '@/storybook/components/Text/Text';
import type { ChatbotMessageFilter } from '@/models/chatbot';
import type { ParsedReasoning } from '../parseReasoning';
import { REASONING_STEPS_TEST_IDS } from './testIds';
import styles from './ReasoningSteps.module.css';

const TOOL_STEP_PATTERN = /^(Using tool:\s*)(.+)$/i;

interface ReasoningStepsProps {
  reasoning: ParsedReasoning;
  filters: ReadonlyArray<ChatbotMessageFilter>;
  hasResponse: boolean;
}

interface ToolStep {
  prefix: string;
  toolName: string;
}

function getToolStep(step: string): ToolStep | null {
  const match = TOOL_STEP_PATTERN.exec(step);

  if (!match) {
    return null;
  }

  return {
    prefix: match[1],
    toolName: match[2].trim(),
  };
}

const ReasoningStepItem: FC<{ step: string }> = ({ step }) => {
  const toolStep = getToolStep(step);

  if (!toolStep) {
    return (
      <Text as="span" className={styles.stepText}>
        {step}
      </Text>
    );
  }

  return (
    <div className={styles.stepInline}>
      <Text as="span" className={styles.stepPrefix}>
        {toolStep.prefix}
      </Text>
      <Text as="span" className={styles.toolName} data-testid={REASONING_STEPS_TEST_IDS.toolName}>
        {toolStep.toolName}
      </Text>
    </div>
  );
};

const ReasoningSteps: FC<ReasoningStepsProps> = ({ reasoning, filters, hasResponse }) => {
  const { t } = useTranslation();
  const { statusText, steps } = reasoning;
  const hasFilters = filters.length > 0;
  const hasSummary = Boolean(statusText) || hasFilters;
  const hasSteps = steps.length > 0;
  const hasContent = hasSummary || hasSteps;

  if (!hasContent) {
    return null;
  }

  return (
    <div className={styles.container} data-testid={REASONING_STEPS_TEST_IDS.container}>
      {hasSummary ? (
        <div className={styles.summaryRow} data-testid={REASONING_STEPS_TEST_IDS.statusRow}>
          <div className={styles.summaryLead}>
            {statusText ? (
              <Text as="span" className={styles.statusText} data-testid={REASONING_STEPS_TEST_IDS.statusText}>
                {statusText}
              </Text>
            ) : null}
            {statusText && hasFilters ? ' ' : null}
            {hasFilters ? (
              <Text as="span" className={styles.parsingFiltersLabel}>
                {t('components.aiChatDrawer.reasoning.parsingFiltersLabel')}
              </Text>
            ) : null}
          </div>
          <AIFilterChips
            filters={filters}
            className={styles.filterChipList}
            data-testid={REASONING_STEPS_TEST_IDS.filterChipList}
          />
        </div>
      ) : null}
      {hasSteps ? (
        <ul className={styles.stepList} data-testid={REASONING_STEPS_TEST_IDS.stepList}>
          {steps.map((step, index) => (
            <li key={index} className={styles.stepItem} data-testid={REASONING_STEPS_TEST_IDS.step}>
              <ReasoningStepItem step={step} />
            </li>
          ))}
        </ul>
      ) : null}
      {hasResponse ? <hr className={styles.separator} data-testid={REASONING_STEPS_TEST_IDS.separator} /> : null}
    </div>
  );
};

export default memo(ReasoningSteps);
